import { useLocation, useNavigate } from 'react-router-dom';
import { findNavItem } from '@/config/navigation';
import { Construction, ArrowLeft, Sparkles, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

/**
 * Lightweight "Coming Soon" page used only as a final fallback when a
 * navigation entry doesn't yet have a real page mounted.
 *
 * Iter-145: surfaces a useful "where authoring happens" note + redirect
 * targets so users are never left at a dead-end. Quick Links rail
 * (provided by AppLayout/RailedLayout) renders alongside this page.
 */

/* Per-area redirect map — when a sub-module isn't built, point the user to
 * the closest parent page where authoring actually happens. */
const REDIRECT_HINTS = [
  { match: '/app/cld/fm', target: '/app/cld/manual-initiation?type=FORCE_MAJEURE',
    label: 'Force Majeure intake (in Claim Initiation)' },
  { match: '/app/cld/ld', target: '/app/cld/register',
    label: 'Claims & LD Register (configure LD per contract)' },
  { match: '/app/cld/evidence', target: '/app/cld/register',
    label: 'Claim Detail → Evidence tab' },
  { match: '/app/cld/dispute', target: '/app/cld/register',
    label: 'Claims Register (dispute stages tab)' },
  { match: '/app/cld/quantum', target: '/app/cld/register',
    label: 'Claim Detail → Quantum tab' },
  { match: '/app/cld/analytics', target: '/app/cld/register',
    label: 'Claims Register · Analytics view' },
  { match: '/app/cld', target: '/app/cld/register',
    label: 'Claim Register & Dashboard' },
  { match: '/app/cm', target: '/app/cm/repository',
    label: 'Contract Repository' },
];

function pickHint(pathname) {
  return REDIRECT_HINTS.find(h => pathname.startsWith(h.match));
}

export default function PlaceholderPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const info = findNavItem(location.pathname);
  const hint = pickHint(location.pathname);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="placeholder-page">
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#1E4E8C]/10 flex items-center justify-center shrink-0">
            <Construction className="w-6 h-6 text-[#1E4E8C]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-[#1E4E8C] font-semibold">
              {info?.module || 'iPCLM'}
            </div>
            <h1 className="text-xl font-heading font-bold tracking-tight mt-0.5"
                data-testid="placeholder-title">
              {info?.subModule || info?.module || 'Module'}
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              This view is a planned drill-down for the parent module. While the dedicated UI is being built,
              you can perform the same actions from the parent page.
            </p>
          </div>
        </div>

        {hint && (
          <button
            type="button"
            onClick={() => navigate(hint.target)}
            data-testid="placeholder-redirect-btn"
            className="mt-5 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 transition group"
          >
            <div className="flex items-center gap-2 text-left">
              <Sparkles className="w-4 h-4 text-amber-700"/>
              <div>
                <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Where to work right now</div>
                <div className="text-sm font-medium text-amber-900">{hint.label}</div>
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-amber-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"/>
          </button>
        )}

        <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1">
          See the <span className="font-medium">Quick Links</span> rail on the left for related modules and
          where the underlying data is authored.
        </div>

        <div className="mt-5 flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}
                  className="gap-2" data-testid="placeholder-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/app')}
                  className="gap-2" data-testid="placeholder-home-btn">
            Dashboard
          </Button>
        </div>
      </Card>
    </div>
  );
}
